import { useState, useEffect, useRef, useMemo, KeyboardEvent, ReactNode } from 'react';
import { Search, X } from 'lucide-react';
import { useCommandPalette, useModalKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';

interface Command {
  id: string;
  label: string;
  icon?: ReactNode;
  action?: () => void;
  keywords?: string[];
  shortcut?: string;
}

interface CommandPaletteProps {
  commands?: Command[];
  placeholder?: string;
}

/**
 * Command Palette component (Cmd+K / Ctrl+K)
 */
export default function CommandPalette({
  commands = [],
  placeholder = 'Rechercher une action...',
}: CommandPaletteProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Register Ctrl+K shortcut
  useCommandPalette(() => setIsOpen(true));

  // Close on Escape
  useModalKeyboardShortcuts({
    onClose: () => setIsOpen(false),
    enabled: isOpen,
  });

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    if (!query) return commands;

    const lowerQuery = query.toLowerCase();
    return commands.filter((cmd) => {
      const labelMatch = cmd.label.toLowerCase().includes(lowerQuery);
      const keywordsMatch = cmd.keywords?.some((kw) =>
        kw.toLowerCase().includes(lowerQuery)
      );
      return labelMatch || keywordsMatch;
    });
  }, [commands, query]);

  // Reset selection when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands.length]);

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredCommands.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          executeCommand(filteredCommands[selectedIndex]);
        }
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.children[selectedIndex] as HTMLElement | undefined;
      selectedEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const executeCommand = (command: Command) => {
    setIsOpen(false);
    command.action?.();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-start justify-center pt-[20vh] z-50"
      onClick={() => setIsOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Palette de commandes"
    >
      <div
        className="bg-white dark:bg-dark-card rounded-lg shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center px-4 py-3 border-b dark:border-dark-border">
          <Search size={20} className="text-gray-400 dark:text-dark-muted mr-3" />
          <input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent outline-none text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-muted"
            aria-label="Rechercher une commande"
          />
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Command List */}
        <div
          ref={listRef}
          className="max-h-72 overflow-y-auto"
          role="listbox"
        >
          {filteredCommands.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-dark-muted">
              Aucune commande trouvée
            </div>
          ) : (
            filteredCommands.map((command, index) => (
              <div
                key={command.id}
                onClick={() => executeCommand(command)}
                className={`flex items-center px-4 py-3 cursor-pointer ${
                  index === selectedIndex
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-dark-border'
                }`}
                role="option"
                aria-selected={index === selectedIndex}
              >
                {command.icon && (
                  <span className="mr-3 text-gray-400 dark:text-dark-muted">
                    {command.icon}
                  </span>
                )}
                <span className="text-gray-900 dark:text-dark-text">
                  {command.label}
                </span>
                {command.shortcut && (
                  <span className="ml-auto text-xs text-gray-400 dark:text-dark-muted bg-gray-100 dark:bg-dark-border px-2 py-1 rounded">
                    {command.shortcut}
                  </span>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t dark:border-dark-border bg-gray-50 dark:bg-dark-border text-xs text-gray-500 dark:text-dark-muted flex items-center justify-between">
          <span>↑↓ pour naviguer</span>
          <span>↵ pour sélectionner</span>
          <span>Esc pour fermer</span>
        </div>
      </div>
    </div>
  );
}
